import React, { useEffect, useState } from 'react';
import {
  Button,
  SelectInputFormik,
  TextAreaFormik,
  Typography
} from 'components/atoms';
import { NewBreadCrumb } from 'components/molecules';
import { ViewScreen } from 'components/template';
import { Form, Formik } from 'formik';
import './index.scss';
import { RootState, useAppDispatch, useAppSelector } from 'store';
import {
  updateLeaveRequestStatus,
  updateLeaveApprovals,
  validateLeave,
  clearValidateResponse
} from 'store/Leaves';
import { LeaveApprovalType, LeaveStatus, UserRoles } from 'models/enums';
import { toast } from 'react-toastify';
import { useQuery } from 'services/hooks';
import Chip from '@mui/material/Chip';
import moment from 'moment';
import LogoSVG from 'components/atoms/LogoSVG';
import { Model, ModalBody, ModalHeader } from 'components/atoms';
import { getEmployees } from 'store/MasterData';
import { request } from 'services/apis';
import {
  getMeaningfulApprovals,
  getApprovalStatusLabel,
  getRespondPageBadgeClassName
} from 'utils/leaveApprovals';
import { LeaveContextCalendar } from 'components/molecules/LeaveContextCalendar';

interface Holiday {
  date: string;
  description?: string;
  event?: string;
}

export const ApproveLeaveRequest = () => {
  const query = useQuery();
  const leaveId = query.get('leaveId');
  const updated = query.get('updated');
  const leaveValidate = useAppSelector((state: RootState) => {
    return state.leaves.validateLeave;
  });
  const leaveDetails = leaveValidate?.latestLeaveInfo;
  const isLatestLeave = leaveDetails?.isLatest !== false;
  const isCancellationReview = leaveDetails?.leaveStatus === 3;
  const pendingApprovalId = leaveValidate?.pendingApprovalId as
    | string
    | undefined;
  const pendingApprovalType = leaveValidate?.pendingApprovalType as
    | number
    | undefined;
  const userRole = useAppSelector(
    (state) => state.auth.user.authData?.data?.userRole
  );
  const isHrActor = userRole === UserRoles.ADMIN;
  const isManagerActor = userRole === UserRoles.MANAGER;
  const canSubmitDecision = isHrActor
    ? isLatestLeave
    : isLatestLeave && Boolean(pendingApprovalId);
  const isCancellationAction =
    isCancellationReview ||
    pendingApprovalType === LeaveApprovalType.CANCELLATION;
  const meaningfulApprovals = getMeaningfulApprovals(
    leaveValidate?.leaveApprovals ?? []
  );
  const status = useAppSelector((state: RootState) => {
    return state.leaves.status ? state.leaves.status : '';
  });

  const employees = useAppSelector((state) => state.masterData.employees);

  const updateLeaveStatusState = useAppSelector((state: RootState) => {
    return state.leaves.updateLeaveStatusState
      ? state.leaves.updateLeaveStatusState
      : '';
  });

  useEffect(() => {
    setDecisionSubmitted(false);
  }, [leaveId, updated]);

  const dispatch = useAppDispatch();
  const [decisionSubmitted, setDecisionSubmitted] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [publicHolidays, setPublicHolidays] = useState<Holiday[]>([]);

  useEffect(() => {
    const requestObject = {
      leaveId,
      updated
    };
    dispatch(validateLeave(requestObject));
    dispatch(getEmployees());

    return () => {
      dispatch(clearValidateResponse());
    };
  }, [dispatch, leaveId, updated]);

  useEffect(() => {
    if (!showCalendarModal || publicHolidays.length > 0) return;

    const loadPublicHolidays = async () => {
      try {
        const response = await request.get<Holiday[]>('/Holiday/public-holidays');
        setPublicHolidays(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Failed to load public holidays:', error);
      }
    };

    loadPublicHolidays();
  }, [publicHolidays.length, showCalendarModal]);

  function findEmpName(id: string) {
    if (!employees || employees.length === 0) return '';
    const employee = employees.find((emp) => emp.employeeId === id);
    return employee ? employee.employeeName : '';
  }

  const headerItem1 = (
    <>
      <Typography type="heading5" text="Aventude Pvt Ltd" />
      <NewBreadCrumb />
    </>
  );

  // initial values for form fields
  const formInitialValues = { dropdown: 'Approved', adminReason: '' };

  // validation for form
  const validateFields = (values: any) => {
    const errors = {} as any;
    if (values.dropdown === 'Rejected' && values.adminReason === '')
      errors.adminReason = 'Please specify reason for rejecting leave request';
  };

  // request object

  // method for submitting the form
  const modalOnSubmit = async (values: any) => {
    if (isManagerActor && pendingApprovalId) {
      const decisionStatus =
        values.dropdown === 'Approved'
          ? LeaveStatus.APPROVED
          : LeaveStatus.REJECTED;

      await dispatch(
        updateLeaveApprovals({
          id: pendingApprovalId,
          status: decisionStatus,
          comments: values.adminReason || '',
          isCancellation: isCancellationAction,
          userRole
        })
      ).unwrap();

      setDecisionSubmitted(true);
      dispatch(validateLeave({ leaveId, updated }));
      return;
    }

    const submitData = new FormData();
    submitData.append('leaveId', leaveId || '');
    submitData.append('status', values.dropdown || '');
    submitData.append('adminReason', values.adminReason || '');

    await dispatch(
      updateLeaveRequestStatus({
        formData: submitData,
        isCancellation: isCancellationAction
      })
    );
    setDecisionSubmitted(true);
  };

  const openExistingFile = (url: string, fileName: string) => {
    try {
      if (!url || url.trim() === '') {
        toast.error('Unable to open attachment: Invalid URL');
        console.error('Invalid attachment URL:', url);
        return;
      }

      // Log for debugging
      console.log('Opening attachment:', fileName, 'URL:', url);

      const newWindow = window.open(url, '_blank');

      // Check if window was blocked
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        toast.error('Pop-up blocked. Please allow pop-ups for this site to view attachments.');
      }
    } catch (error) {
      console.error('Error opening attachment:', error);
      toast.error('Failed to open attachment. Please try again.');
    }
  };

  // form for approving / rejecting a leave request
  const mainItem2 = (
    <div className="approve-container">
      <Formik
        initialValues={formInitialValues}
        enableReinitialize
        validate={validateFields}
        validateOnChange
        onSubmit={(values) => {
          modalOnSubmit(values);
        }}
      >
        {({ values, setFieldValue }: any) => (
          <div className="approve-screen">
            <div className="three-column-layout">
              {/* Column 1: Leave Details */}
              <div className="leave-details-column">
                <div className="section-header section-header--with-action">
                  <Typography type="heading5" text="Leave Request Details" />
                  {leaveDetails && (
                    <button
                      type="button"
                      className="calendar-trigger-btn"
                      onClick={() => setShowCalendarModal(true)}
                    >
                      See Calendar
                    </button>
                  )}
                </div>
                {leaveDetails && (
                  <div className="leave-info-list">
                    <div className="info-item">
                      <Typography type="small_bold" text="Employee Name:" />
                      <Typography
                        type="small_regular"
                        text={leaveDetails.user || ''}
                      />
                    </div>
                    <div className="info-item">
                      <Typography type="small_bold" text="Start Date:" />
                      <Typography
                        type="small_regular"
                        text={
                          leaveDetails.startDate
                            ? moment(leaveDetails.startDate).format(
                                'MMMM DD, YYYY'
                              )
                            : ''
                        }
                      />
                    </div>
                    <div className="info-item">
                      <Typography type="small_bold" text="End Date:" />
                      <Typography
                        type="small_regular"
                        text={
                          leaveDetails.endDate
                            ? moment(leaveDetails.endDate).format(
                                'MMMM DD, YYYY'
                              )
                            : ''
                        }
                      />
                    </div>
                    <div className="info-item">
                      <Typography type="small_bold" text="Leave Type:" />
                      <Typography
                        type="small_regular"
                        text={leaveDetails.leaveType || ''}
                      />
                    </div>
                    <div className="info-item">
                      <Typography type="small_bold" text="Leave Session:" />
                      <Typography
                        type="small_regular"
                        text={leaveDetails.session || ''}
                      />
                    </div>
                    <div className="info-item">
                      <Typography type="small_bold" text="Leave Reason:" />
                      <Typography
                        type="small_regular"
                        text={leaveDetails.reason || ''}
                      />
                    </div>
                    <div className="info-item">
                      <Typography
                        type="small_bold"
                        text="Applied No of Days:"
                      />
                      <Typography
                        type="small_regular"
                        text={leaveDetails.noOfDays ?? ''}
                      />
                    </div>
                    {leaveDetails.leaveType !== 'Medical Leave' && (
                      <div className="info-item">
                        <Typography
                          type="small_bold"
                          text="Available Leave Count:"
                        />
                        <Typography
                          type="small_regular"
                          text={leaveDetails.availableLeaveCount ?? ''}
                        />
                      </div>
                    )}
                    {leaveDetails.leaveType === 'Medical Leave' && (
                      <div className="info-item">
                        <Typography
                          type="small_bold"
                          text="Used Medical Leaves:"
                        />
                        <Typography
                          type="small_regular"
                          text={leaveDetails.noOfMedicalTaken ?? ''}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Attachments in first column */}
                {Array.isArray(leaveDetails?.attachments) &&
                  leaveDetails.attachments.length > 0 && (
                    <div className="attachments-section">
                      <div className="info-item">
                        <Typography type="small_bold" text="Attachments:" />
                        <Typography
                          type="small_regular"
                          text={`${leaveDetails.attachments.length} file(s)`}
                        />
                      </div>
                      <div className="attachments-list">
                        {leaveDetails.attachments.map(
                          (attachment: any, index: number) => {
                            const fileName = attachment?.name || attachment?.Name || `Attachment ${index + 1}`;
                            const blobUrl = process.env.REACT_APP_BLOB_STORAGE_URL || '';
                            const fullUrl = blobUrl + fileName;

                            return (
                              <div key={attachment?.id || index} className="attachment-chip">
                                <Chip
                                  label={fileName}
                                  onClick={() => openExistingFile(fullUrl, fileName)}
                                  clickable
                                  variant="outlined"
                                  color="primary"
                                  icon={<i className="ri-file-download-line"></i>}
                                />
                              </div>
                            );
                          }
                        )}
                      </div>
                    </div>
                  )}
                {/* Show message if medical leave but no attachments */}
                {leaveDetails?.leaveType === 'Medical Leave' &&
                  (!Array.isArray(leaveDetails?.attachments) ||
                    leaveDetails.attachments.length === 0) && (
                    <div className="attachments-section">
                      <div className="info-item">
                        <Typography
                          type="small_bold"
                          text="Attachments:"
                        />
                        <Typography
                          type="small_regular"
                          text="No attachments found"
                          className="text-warning"
                        />
                      </div>
                    </div>
                  )}
              </div>

              {/* Column 2: Leave Approvals */}
              <div className="leave-approvals-column">
                <div className="section-header">
                  <Typography type="heading5" text="Leave Approvals" />
                </div>
                {meaningfulApprovals.length > 0 ? (
                    <div className="approvals-list">
                      {meaningfulApprovals.map(
                        (approval: any, idx: number) => (
                          <div
                            key={approval.id || idx}
                            className="approval-item"
                          >
                            <div className="info-item">
                              <Typography
                                type="small_bold"
                                text="Approver:"
                              />
                              <Typography
                                type="small_regular"
                                text={findEmpName(approval.approverId)}
                              />
                            </div>
                            <div className="info-item status-row">
                              <Typography type="small_bold" text="Status:" />
                              <div className="status-badge-container">
                                <span
                                  className={getRespondPageBadgeClassName(
                                    approval
                                  )}
                                >
                                  {getApprovalStatusLabel(approval)}
                                </span>
                              </div>
                            </div>
                            {approval.comments && (
                              <div className="info-item">
                                <Typography
                                  type="small_bold"
                                  text="Comments:"
                                />
                                <Typography
                                  type="small_regular"
                                  text={approval.comments}
                                />
                              </div>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  ) : (
                    <Typography
                      type="small_regular"
                      text="No approval decisions recorded yet."
                    />
                  )}
              </div>

              {/* Column 3: Approval Decision Form */}
              <div className="approval-form-column">
                <Form className="approval-form">
                  <div className="section-header">
                    <Typography
                      type="heading5"
                      text={
                        isCancellationAction
                          ? 'Cancellation Decision'
                          : 'Approval Decision'
                      }
                    />
                  </div>
                  {!isLatestLeave && leaveDetails?.comment && (
                    <div className="leave-status-notice" role="status">
                      {leaveDetails.comment}
                    </div>
                  )}
                  <div className="form-wrapper">
                    <SelectInputFormik
                      name="dropdown"
                      label={
                        isCancellationAction
                          ? 'Approve / Reject Cancellation'
                          : 'Approve / Reject'
                      }
                      customStyles="custom-dropdown"
                      error={false}
                      required={true}
                      handleChange={(event: any) => {
                        setFieldValue('dropdown', event.target.value);
                      }}
                    >
                      <option value="Approved">
                        {isCancellationAction
                          ? 'Approve Cancellation'
                          : 'Approve'}
                      </option>
                      <option value="Rejected">
                        {isCancellationAction
                          ? 'Reject Cancellation'
                          : 'Reject'}
                      </option>
                    </SelectInputFormik>
                  </div>
                  <div className="textarea-wrapper">
                    <TextAreaFormik
                      id="adminReason"
                      name="adminReason"
                      rows={5}
                      placeholder={
                        values.dropdown === 'Rejected'
                          ? 'Enter reason for rejection'
                          : 'Enter reason for approval (Optional)'
                      }
                      required={values.dropdown === 'Rejected'}
                      handleChange={(event: any) => {
                        setFieldValue('adminReason', event.target.value);
                      }}
                    />
                  </div>
                  <div className="button-wrapper">
                    <Button
                      type="success"
                      htmlType="submit"
                      size="lg"
                      disabled={
                        !canSubmitDecision ||
                        decisionSubmitted ||
                        updateLeaveStatusState === 'fulfilled' ||
                        (values.dropdown === 'Rejected' &&
                          values.adminReason === '')
                      }
                    >
                      SUBMIT
                    </Button>
                  </div>
                </Form>
              </div>
            </div>
          </div>
        )}
      </Formik>

      <Model
        modalSize="lg"
        showModal={showCalendarModal}
        onClose={() => setShowCalendarModal(false)}
      >
        <ModalHeader
          headerTitle="Leave Period Calendar"
          onClose={() => setShowCalendarModal(false)}
        />
        <ModalBody>
          {leaveDetails?.startDate && leaveDetails?.endDate ? (
            <LeaveContextCalendar
              startDate={leaveDetails.startDate}
              endDate={leaveDetails.endDate}
              holidays={publicHolidays}
            />
          ) : (
            <Typography type="small_regular" text="Leave date range is unavailable." />
          )}
        </ModalBody>
      </Model>
    </div>
  );

  return (
    <>
      {status === 'pending' && (
        <div className="loading-container">
          <LogoSVG />
        </div>
      )}
      {status === 'fulfilled' && (
        <ViewScreen
          portalTitle="Leave"
          headerItem1={headerItem1}
          mainItem2={mainItem2}
        />
      )}
    </>
  );
};
